import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahar3boh653ivfvnq2g';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function SubscribeBanner() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const payload = new FormData(formEl);

    const honeypot = String(payload.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      formEl.reset();
      return;
    }
    payload.delete('website_alt');

    setStatus('loading');
    setFormError('');

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(isSpam ? '提交被拦截，请稍后重试' : serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请检查网络后重试');
    }
  };

  return (
    <section id="subscribe" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100 scroll-mt-20">
      <div className="relative w-full rounded-lg overflow-hidden bg-foreground-950">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20warm%20crimson%20and%20amber%20gradient%20with%20soft%20glowing%20bokeh%20particles%2C%20cinematic%20night%20atmosphere%2C%20editorial%20illustration%2C%20minimal%20elegant&width=1600&height=700&seq=gossip-subscribe-bg&orientation=landscape"
          alt="订阅瓜圈速报背景"
          title="订阅瓜圈速报"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/80 to-foreground-950/50"></div>

        <div className="relative w-full px-6 md:px-12 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-background-50">瓜圈速报 · 订阅提醒</h2>
            <p className="mt-3 text-sm md:text-base text-background-200 leading-relaxed max-w-md">
              订阅后，事件反转、最新回应、后续进展会在第一时间推到你面前。追瓜不用一直悬着心。
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-300">
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-accent-400"></i>
                反转第一时间提醒
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-accent-400"></i>
                每日大瓜汇总
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-accent-400"></i>
                随时可退订
              </span>
            </div>
          </div>

          <div>
            <form
              id="gossip-subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg bg-background-50 p-5 md:p-6"
            >
              <label htmlFor="sub-email" className="block text-sm font-medium text-foreground-800">
                留下你的邮箱
              </label>
              <div className="mt-2 flex flex-col sm:flex-row gap-2">
                <input
                  id="sub-email"
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="flex-1 rounded-md border border-background-300/80 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/60"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 dark:text-foreground-950 hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中
                    </>
                  ) : (
                    <>
                      <i className="ri-notification-3-line"></i>
                      立即订阅
                    </>
                  )}
                </button>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="input-guard-extra"
              />

              {status === 'success' && (
                <p className="mt-3 text-sm text-primary-600 flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-line"></i>
                  订阅成功，反转第一时间通知你
                </p>
              )}
              {status === 'error' && (
                <p className="mt-3 text-sm text-foreground-700 flex items-center gap-1.5">
                  <i className="ri-error-warning-line text-primary-600"></i>
                  {formError}
                </p>
              )}

              <p className="mt-3 text-xs text-foreground-500">我们只用来推送速报，不会分享你的邮箱。</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}